import type { ApiResult, RetryOptions } from "admin/api/api_result";
import { requestResult } from "admin/api/api_result";
import dayjs from "dayjs";
import update from "immutability-helper";
import { toBigInt } from "libs/bigint_helpers";
import type { RequestOptions, RequestOptionsWithData } from "libs/request";
import Request from "libs/request";
import ResumableUpload from "libs/resumable_upload/resumable_upload";
import type { Message } from "libs/toast";
import Toast from "libs/toast";
import {
  getAdaptToTypeFunctionFromList,
  millisecondsToMinutes,
  minutesToMilliseconds,
  parseMaybe,
  retryAsyncFunction,
} from "libs/utils";
import window from "libs/window";
import memoize from "lodash-es/memoize";
import zip from "lodash-es/zip";
import messages from "messages";
import {
  type AdditionalCoordinate,
  type AnnotationCollaborationMode,
  type AnnotationIdDomain,
  type AnnotationLayerDescriptor,
  AnnotationLayerEnum,
  type AnnotationViewConfiguration,
  type APIAnnotation,
  type APIAnnotationInfo,
  type APIAnnotationType,
  type APIAnnotationVisibility,
  type APIAvailableTasksReport,
  type APIBuildInfoWk,
  type APICompoundType,
  type APIConnectomeFile,
  type APIDataSource,
  type APIDataSourceId,
  type APIDataStore,
  type APIDataset,
  type APIDatasetCompact,
  type APIFeatureToggles,
  type APIHistogramData,
  type APIMagRestrictions,
  type APIMapping,
  type APIMaybeUnimportedDataset,
  type APIMeshFileInfo,
  type APIOrganization,
  type APIProject,
  type APIProjectCreator,
  type APIProjectProgressReport,
  type APIProjectUpdater,
  type APIProjectWithStatus,
  type APIPublication,
  type APIScript,
  type APIScriptCreator,
  type APIScriptUpdater,
  type APIStorageDetailEntry,
  type APITaskType,
  type APITeam,
  type APITimeInterval,
  type APITimeTrackingPerAnnotation,
  type APITimeTrackingPerUser,
  type APITimeTrackingSpan,
  type APITracingStore,
  type APITracingStoreAnnotation,
  type APIUpdateActionBatch,
  type APIUser,
  type APIUserCompact,
  type APIUserLoggedTime,
  type APIUserTheme,
  type ExperienceDomainList,
  type LayerLink,
  type MaintenanceInfo,
  type SegmentCovarianceMatrix,
  type SegmentStatisticsFileInfo,
  type ServerEditableMapping,
  type ServerTracing,
  type ShortLink,
  type TracingType,
  type VoxelSize,
  type VoxelyticsChunkStatistics,
  type VoxelyticsLogLine,
  type VoxelyticsWorkflowListing,
  type VoxelyticsWorkflowReport,
  type ZarrPrivateLink,
} from "types/api_types";
import { enforceValidatedDatasetViewConfiguration } from "types/schemas/dataset_view_configuration_defaults";
import type { DatasourceConfiguration } from "types/schemas/datasource.types";
import type { ArbitraryObject, EmptyObject } from "types/type_utils";
import type { AnnotationTypeFilterEnum, LOG_LEVELS, MappingType, Vector3 } from "viewer/constants";
import Constants, { AnnotationStateFilterEnum } from "viewer/constants";
import type BoundingBox from "viewer/model/bucket_data_handling/bounding_box";
import {
  getDataOrTracingStoreUrl,
  type LayerSourceInfo,
} from "viewer/model/bucket_data_handling/wkstore_helper";
import {
  parseProtoAnnotation,
  parseProtoListOfLong,
  parseProtoTracing,
  serializeProtoListOfLong,
} from "viewer/model/helpers/proto_helpers";
import type {
  DatasetConfiguration,
  Mapping,
  PartialDatasetConfiguration,
  SaveQueueEntry,
  StoreAnnotation,
  UserConfiguration,
  VolumeTracing,
} from "viewer/store";
import type { KeyboardShortcutsMap } from "viewer/view/keyboard_shortcuts/keyboard_shortcut_types";
import { assertResponseLimit } from "./api/api_utils";
import { getDatasetIdFromNameAndOrganization } from "./api/disambiguate_legacy_routes";
import { getOrganization } from "./api/organization";
import { doWithToken, refreshToken } from "./api/token";

export * from "./api/jobs";
export * as meshApi from "./api/mesh";
export * from "./api/token";

type NewTeam = {
  readonly name: string;
};

export function sendAnalyticsEvent(
  eventType: string,
  eventProperties: Record<string, any> = {},
): void {
  // Note that the Promise from sendJSONReceiveJSON is not awaited or returned here,
  // since failing analytics events should not have an impact on the application logic.
  Request.sendJSONReceiveJSON(`/api/analytics/${eventType}`, {
    method: "POST",
    data: eventProperties,
    showErrorToast: false,
  });
}
export function sendFailedRequestAnalyticsEvent(
  requestType: string,
  error: Record<string, any>,
  requestProperties: ArbitraryObject,
): void {
  const eventProperties = {
    request_type: requestType,
    request_properties: requestProperties,
    status: error.status || 0,
    messages: error.messages || [],
  };
  sendAnalyticsEvent("request_failed", eventProperties);
}

// ### Users
export async function loginUser(
  formValues: {
    email: string;
    password: string;
  },
  options: RequestOptions = {},
  // The requester below performs the login POST plus follow-up reads as one
  // unit. Since a login POST isn't idempotent, this must not be retried as a
  // whole -- otherwise a failing getActiveUser/getOrganization call after a
  // successful login would cause a duplicate login POST.
  retryOptions: RetryOptions = { retries: 0 },
): Promise<ApiResult<[APIUser, APIOrganization]>> {
  return requestResult(
    async (adaptedOptions) => {
      await Request.sendJSONReceiveJSON("/api/auth/login", {
        ...adaptedOptions,
        data: formValues,
      });
      const activeUser = await getActiveUser();
      const organization = await getOrganization(activeUser.organization);

      return [activeUser, organization];
    },
    options,
    retryOptions,
  );
}

export async function logoutUser(
  options: RequestOptions = {},
  retryOptions?: RetryOptions,
): Promise<ApiResult<string>> {
  return requestResult(
    (adaptedOptions) =>
      Request.receiveJSON("/api/auth/logout", { ...adaptedOptions, method: "POST" }),
    options,
    retryOptions,
  );
}

export async function logoutUserEverywhere(
  options: RequestOptions = {},
  retryOptions?: RetryOptions,
): Promise<ApiResult<void>> {
  return requestResult(
    (adaptedOptions) =>
      Request.receiveJSON("/api/auth/logoutEverywhere", { ...adaptedOptions, method: "POST" }),
    options,
    retryOptions,
  );
}

export async function getUsers(
  options: RequestOptions = {},
  retryOptions?: RetryOptions,
): Promise<ApiResult<Array<APIUser>>> {
  return requestResult(
    async (adaptedOptions) => {
      const users = await Request.receiveJSON("/api/users", adaptedOptions);
      assertResponseLimit(users);
      return users;
    },
    options,
    retryOptions,
  );
}

export async function getTeamManagerOrAdminUsers(
  options: RequestOptions = {},
  retryOptions?: RetryOptions,
): Promise<ApiResult<Array<APIUser>>> {
  return requestResult(
    async (adaptedOptions) => {
      const users = await Request.receiveJSON(
        "/api/users?isTeamManagerOrAdmin=true",
        adaptedOptions,
      );
      assertResponseLimit(users);
      return users;
    },
    options,
    retryOptions,
  );
}

export async function getAdminUsers(
  options: RequestOptions = {},
  retryOptions?: RetryOptions,
): Promise<ApiResult<Array<APIUser>>> {
  return requestResult(
    async (adaptedOptions) => {
      const users = await Request.receiveJSON("/api/users?isAdmin=true", adaptedOptions);
      assertResponseLimit(users);
      return users;
    },
    options,
    retryOptions,
  );
}

export async function getEditableUsers(
  options: RequestOptions = {},
  retryOptions?: RetryOptions,
): Promise<ApiResult<Array<APIUser>>> {
  return requestResult(
    async (adaptedOptions) => {
      const users = await Request.receiveJSON("/api/users?isEditable=true", adaptedOptions);
      assertResponseLimit(users);
      return users;
    },
    options,
    retryOptions,
  );
}

export async function getUser(
  userId: string,
  options: RequestOptions = {},
  retryOptions?: RetryOptions,
): Promise<ApiResult<APIUser>> {
  return requestResult(
    (adaptedOptions) => Request.receiveJSON(`/api/users/${userId}`, adaptedOptions),
    options,
    retryOptions,
  );
}

export async function updateUser(
  newUser: Partial<APIUser>,
  options: RequestOptions = {},
  retryOptions?: RetryOptions,
): Promise<ApiResult<APIUser>> {
  return requestResult(
    (adaptedOptions) =>
      Request.sendJSONReceiveJSON(`/api/users/${newUser.id}`, {
        ...adaptedOptions,
        method: "PATCH",
        data: newUser,
      }),
    options,
    retryOptions,
  );
}

export function updateNovelUserExperienceInfos(
  user: APIUser,
  novelUserExperienceShape: Record<string, any>,
  options: RequestOptions = {},
  retryOptions?: RetryOptions,
): [APIUser, Promise<ApiResult<APIUser>>] {
  const novelUserExperienceInfos = {
    ...user.novelUserExperienceInfos,
    ...novelUserExperienceShape,
  };
  const newUserSync = { ...user, novelUserExperienceInfos };
  const newUserAsync = requestResult(
    (adaptedOptions) =>
      Request.sendJSONReceiveJSON(`/api/users/${user.id}/novelUserExperienceInfos`, {
        ...adaptedOptions,
        method: "PUT",
        data: novelUserExperienceInfos,
      }),
    options,
    retryOptions,
  );
  return [newUserSync, newUserAsync];
}

export function updateLastTaskTypeIdOfUser(
  userId: string,
  lastTaskTypeId: string,
): Promise<APIUser> {
  return Request.sendJSONReceiveJSON(`/api/users/${userId}/taskTypeId`, {
    method: "PUT",
    data: {
      lastTaskTypeId,
    },
  });
}

export function updateSelectedThemeOfUser(
  userId: string,
  selectedTheme: APIUserTheme,
): Promise<APIUser> {
  return Request.sendJSONReceiveJSON(`/api/users/${userId}/selectedTheme`, {
    method: "PUT",
    data: JSON.stringify(selectedTheme),
  });
}

export async function getAuthToken(): Promise<string> {
  const { token } = await Request.receiveJSON("/api/auth/token");
  return token;
}

export async function revokeAuthToken(): Promise<void> {
  await Request.receiveJSON("/api/auth/token", {
    method: "DELETE",
  });
}

export async function changePassword(data: Record<string, string>): Promise<void> {
  await Request.sendJSONReceiveJSON("/api/auth/changePassword", {
    data: data,
  });
}

// Used only by the webknossos-libs python client, but tested here in the snapshot tests.
export async function getLoggedTimes(userID: string): Promise<Array<APITimeInterval>> {
  const url = `/api/users/${userID}/loggedTime`;
  const response: APIUserLoggedTime = await Request.receiveJSON(url);
  return response.loggedTime;
}

// ### Scripts
export async function getScripts(): Promise<Array<APIScript>> {
  const scripts = await Request.receiveJSON("/api/scripts");
  assertResponseLimit(scripts);
  return scripts;
}

export function getScript(scriptId: string): Promise<APIScript> {
  return Request.receiveJSON(`/api/scripts/${scriptId}`);
}

export function deleteScript(scriptId: string): Promise<void> {
  return Request.receiveJSON(`/api/scripts/${scriptId}`, {
    method: "DELETE",
  });
}

export function createScript(script: APIScriptCreator): Promise<APIScript> {
  return Request.sendJSONReceiveJSON("/api/scripts", {
    data: script,
  });
}

export function updateScript(scriptId: string, script: APIScriptUpdater): Promise<APIScript> {
  return Request.sendJSONReceiveJSON(`/api/scripts/${scriptId}`, {
    method: "PUT",
    data: script,
  });
}
// ### TaskTypes
export async function getTaskTypes(): Promise<Array<APITaskType>> {
  const taskTypes = await Request.receiveJSON("/api/taskTypes");
  assertResponseLimit(taskTypes);
  return taskTypes;
}
export function deleteTaskType(taskTypeId: string): Promise<void> {
  return Request.receiveJSON(`/api/taskTypes/${taskTypeId}`, {
    method: "DELETE",
  });
}
export function getTaskType(taskTypeId: string): Promise<APITaskType> {
  return Request.receiveJSON(`/api/taskTypes/${taskTypeId}`);
}
export function createTaskType(
  taskType: Omit<APITaskType, "id" | "teamName">,
): Promise<APITaskType> {
  return Request.sendJSONReceiveJSON("/api/taskTypes", {
    data: taskType,
  });
}

export function updateTaskType(taskTypeId: string, taskType: APITaskType): Promise<void> {
  return Request.sendJSONReceiveJSON(`/api/taskTypes/${taskTypeId}`, {
    method: "PUT",
    data: taskType,
  });
}

// ### Teams
export async function getTeams(options?: RequestOptions): Promise<Array<APITeam>> {
  const teams = await Request.receiveJSON(
    "/api/teams",
    options ?? {
      doNotInvestigate: true,
    },
  );
  assertResponseLimit(teams);
  return teams;
}

export async function getEditableTeams(): Promise<Array<APITeam>> {
  const teams = await Request.receiveJSON("/api/teams?isEditable=true", {
    doNotInvestigate: true,
  });
  assertResponseLimit(teams);
  return teams;
}

export function createTeam(newTeam: NewTeam): Promise<APITeam> {
  return Request.sendJSONReceiveJSON("/api/teams", {
    data: newTeam,
  });
}

export function deleteTeam(teamId: string): Promise<void> {
  return Request.receiveJSON(`/api/teams/${teamId}`, {
    method: "DELETE",
  });
}

// ### Projects
function transformProject<T extends APIProject | APIProjectWithStatus>(response: T): T {
  return Object.assign({}, response, {
    expectedTime: millisecondsToMinutes(response.expectedTime),
  });
}

export async function getProjects(): Promise<Array<APIProject>> {
  const responses = await Request.receiveJSON("/api/projects");
  assertResponseLimit(responses);
  return responses.map(transformProject);
}
export async function getProjectsWithStatus(): Promise<Array<APIProjectWithStatus>> {
  const responses = await Request.receiveJSON("/api/projects/withStatus");
  assertResponseLimit(responses);
  return responses.map(transformProject);
}
export async function getProjectsForTaskType(
  taskTypeId: string,
): Promise<Array<APIProjectWithStatus>> {
  const responses = await Request.receiveJSON(`/api/taskTypes/${taskTypeId}/projects`);
  assertResponseLimit(responses);
  return responses.map(transformProject);
}
export async function getProject(projectId: string): Promise<APIProject> {
  const project = await Request.receiveJSON(`/api/projects/${projectId}`);
  return transformProject(project);
}
export async function increaseProjectTaskInstances(
  projectId: string,
  delta: number = 1,
): Promise<APIProjectWithStatus> {
  const project = await Request.receiveJSON(
    `/api/projects/${projectId}/incrementEachTasksInstances?delta=${delta}`,
    {
      method: "PATCH",
    },
  );
  return transformProject(project);
}

export function deleteProject(projectId: string): Promise<void> {
  return Request.receiveJSON(`/api/projects/${projectId}`, {
    method: "DELETE",
  });
}
export function createProject(project: APIProjectCreator): Promise<APIProject> {
  const transformedProject = Object.assign({}, project, {
    expectedTime: minutesToMilliseconds(project.expectedTime),
  });
  return Request.sendJSONReceiveJSON("/api/projects", {
    data: transformedProject,
  });
}
export function updateProject(projectId: string, project: APIProjectUpdater): Promise<APIProject> {
  const transformedProject = Object.assign({}, project, {
    expectedTime: minutesToMilliseconds(project.expectedTime),
  });
  return Request.sendJSONReceiveJSON(`/api/projects/${projectId}`, {
    method: "PUT",
    data: transformedProject,
  });
}
export async function pauseProject(projectId: string): Promise<APIProject> {
  const project = await Request.receiveJSON(`/api/projects/${projectId}/pause`, {
    method: "PATCH",
  });
  return transformProject(project);
}
export async function resumeProject(projectId: string): Promise<APIProject> {
  const project = await Request.receiveJSON(`/api/projects/${projectId}/resume`, {
    method: "PATCH",
  });
  return transformProject(project);
}

// ### Private Links

export function createPrivateLink(
  annotationId: string,
  initialExpirationPeriodInDays: number = 30,
): Promise<ZarrPrivateLink> {
  return Request.sendJSONReceiveJSON("/api/zarrPrivateLinks", {
    data: {
      annotation: annotationId,
      expirationDateTime: dayjs().endOf("day").add(initialExpirationPeriodInDays, "days").valueOf(),
    },
  });
}

export function getPrivateLinksByAnnotation(annotationId: string): Promise<Array<ZarrPrivateLink>> {
  return Request.receiveJSON(`/api/zarrPrivateLinks/byAnnotation/${annotationId}`);
}

export function updatePrivateLink(link: ZarrPrivateLink): Promise<ZarrPrivateLink> {
  return Request.sendJSONReceiveJSON(`/api/zarrPrivateLinks/${link.id}`, {
    data: link,
    method: "PUT",
  });
}

export function deletePrivateLink(linkId: string): Promise<{
  messages: Array<Message>;
}> {
  return Request.receiveJSON(`/api/zarrPrivateLinks/${linkId}`, {
    method: "DELETE",
  });
}

// ### Annotations
export function getCompactAnnotationsForUser(
  userId: string,
  isFinished: boolean,
  pageNumber: number = 0,
): Promise<Array<APIAnnotationInfo>> {
  return Request.receiveJSON(
    `/api/users/${userId}/annotations?isFinished=${isFinished.toString()}&pageNumber=${pageNumber}`,
  );
}

export function getReadableAnnotations(
  isFinished: boolean,
  pageNumber: number = 0,
): Promise<Array<APIAnnotationInfo>> {
  return Request.receiveJSON(
    `/api/annotations/readable?isFinished=${isFinished.toString()}&pageNumber=${pageNumber}`,
  );
}

export async function getAnnotationCountForDataset(datasetId: string): Promise<number> {
  const { headers } = await Request.receiveJSONWithHeaders(
    // Only count non-archived annotations to match the default annotation list.
    `/api/annotations/readable?isFinished=false&limit=1&includeTotalCount=true&datasetId=${datasetId}`,
    // Callers treat the count as optional, e.g. when viewing public data without being authorized to list annotations.
    { showErrorToast: false, doNotInvestigate: true },
  );
  const totalCount = headers.get("X-Total-Count");
  return totalCount != null ? Number.parseInt(totalCount, 10) : 0;
}

export function getTeamsForSharedAnnotation(
  typ: string,
  id: string,
  options?: RequestOptions,
): Promise<Array<APITeam>> {
  return Request.receiveJSON(`/api/annotations/${typ}/${id}/sharedTeams`, options);
}

export function updateTeamsForSharedAnnotation(
  typ: string,
  id: string,
  teamIds: Array<string>,
): Promise<Array<APITeam>> {
  return Request.sendJSONReceiveJSON(`/api/annotations/${typ}/${id}/sharedTeams`, {
    data: teamIds,
    method: "PATCH",
  });
}

export function reOpenAnnotation(
  annotationId: string,
  annotationType: APIAnnotationType,
): Promise<APIAnnotation> {
  return Request.receiveJSON(`/api/annotations/${annotationType}/${annotationId}/reopen`, {
    method: "PATCH",
  });
}

export type EditableAnnotation = {
  name: string;
  visibility: APIAnnotationVisibility;
  tags: Array<string>;
  viewConfiguration?: AnnotationViewConfiguration;
};

export function editAnnotation(
  annotationId: string,
  annotationType: APIAnnotationType,
  data: Partial<EditableAnnotation>,
): Promise<void> {
  return Request.sendJSONReceiveJSON(`/api/annotations/${annotationType}/${annotationId}/edit`, {
    data,
    method: "PATCH",
  });
}

export function editLockedState(
  annotationId: string,
  annotationType: APIAnnotationType,
  isLockedByOwner: boolean,
): Promise<APIAnnotation> {
  return Request.receiveJSON(
    `/api/annotations/${annotationType}/${annotationId}/editLockedState?isLockedByOwner=${isLockedByOwner}`,
    {
      method: "PATCH",
    },
  );
}

export function setCollaborationModeForAnnotation(
  annotationId: string,
  annotationType: APIAnnotationType,
  collaborationMode: AnnotationCollaborationMode,
  options?: RequestOptions,
): Promise<void> {
  return Request.receiveJSON(
    `/api/annotations/${annotationType}/${annotationId}/collaborationMode?collaborationMode=${collaborationMode}`,
    {
      method: "PATCH",
      ...options,
    },
  );
}

type AnnotationLayerCreateDescriptor = {
  typ: AnnotationLayerEnum;
  name: string | null | undefined;
  autoFallbackLayer?: boolean;
  fallbackLayerName?: string | null | undefined;
  mappingName?: string | null | undefined;
  magRestrictions?: APIMagRestrictions | null | undefined;
};

export function finishAnnotation(
  annotationId: string,
  annotationType: APIAnnotationType,
): Promise<APIAnnotation> {
  return Request.receiveJSON(
    `/api/annotations/${annotationType}/${annotationId}/finish?timestamp=${Date.now()}`,
    {
      method: "PATCH",
    },
  );
}

export function resetAnnotation(
  annotationId: string,
  annotationType: APIAnnotationType,
): Promise<APIAnnotation> {
  return Request.receiveJSON(`/api/annotations/${annotationType}/${annotationId}/reset`, {
    method: "PUT",
  });
}

export function deleteAnnotation(
  annotationId: string,
  annotationType: APIAnnotationType,
): Promise<{
  messages: Array<Message>;
}> {
  return Request.receiveJSON(`/api/annotations/${annotationType}/${annotationId}`, {
    method: "DELETE",
  });
}

export function finishAllAnnotations(selectedAnnotationIds: Array<string>): Promise<{
  messages: Array<Message>;
}> {
  return Request.sendJSONReceiveJSON(
    `/api/annotations/Explorational/finish?timestamp=${Date.now()}`,
    {
      method: "PATCH",
      data: {
        annotations: selectedAnnotationIds,
      },
    },
  );
}

export function duplicateAnnotation(
  annotationId: string,
  annotationType: APIAnnotationType,
): Promise<APIAnnotation> {
  const url = `/api/annotations/${annotationType}/${annotationId}/duplicate`;
  return Request.receiveJSON(url, {
    method: "POST",
  });
}

// Note: The returned annotation object contains viewConfiguration. An arbitrary object which should be schema checked before used.
// Currently, the only location where this is needed is during initialization.
export async function getUnversionedAnnotationInformation(
  annotationId: string,
  options: RequestOptions = {},
  retryOptions?: RetryOptions,
): Promise<ApiResult<APIAnnotation>> {
  const infoUrl = `/api/annotations/${annotationId}/info?timestamp=${Date.now()}`;
  return requestResult(
    async (adaptedOpts) => {
      const annotationWithMessages = await Request.receiveJSON(infoUrl, adaptedOpts);

      // Extract the potential messages property before returning the task to avoid
      // failing e2e tests in annotations.e2e.ts
      const { messages: _messages, ...annotation } = annotationWithMessages;
      return annotation;
    },
    options,
    retryOptions,
  );
}

export async function getAnnotationCompoundInformation(
  annotationId: string,
  annotationType: APICompoundType,
  options: RequestOptions = {},
): Promise<APIAnnotation> {
  const infoUrl = `/api/annotations/${annotationType}/${annotationId}/info?timestamp=${Date.now()}`;
  const annotationWithMessages = await Request.receiveJSON(infoUrl, options);

  // Extract the potential messages property before returning the task to avoid
  // failing e2e tests in annotations.e2e.ts
  const { messages: _messages, ...annotation } = annotationWithMessages;
  return annotation;
}

export function getEmptySandboxAnnotationInformation(
  datasetId: string,
  tracingType: TracingType,
  sharingToken?: string | null | undefined,
  options: RequestOptions = {},
): Promise<APIAnnotation> {
  const sharingTokenSuffix = sharingToken != null ? `?sharingToken=${sharingToken}` : "";
  const infoUrl = `/api/datasets/${datasetId}/sandbox/${tracingType}${sharingTokenSuffix}`;
  return Request.receiveJSON(infoUrl, options);
}

export function createExplorational(
  datasetId: string,
  typ: TracingType,
  autoFallbackLayer: boolean,
  fallbackLayerName?: string | null | undefined,
  mappingName?: string | null | undefined,
  magRestrictions?: APIMagRestrictions | null | undefined,
  options: RequestOptions = {},
): Promise<APIAnnotation> {
  const url = `/api/datasets/${datasetId}/createExplorational`;
  let layers: Array<AnnotationLayerCreateDescriptor> = [];

  if (typ === "skeleton") {
    layers = [
      {
        typ: AnnotationLayerEnum.Skeleton,
        name: "Skeleton",
      },
    ];
  } else if (typ === "volume") {
    layers = [
      {
        typ: AnnotationLayerEnum.Volume,
        name: fallbackLayerName,
        fallbackLayerName,
        autoFallbackLayer,
        mappingName,
        magRestrictions,
      },
    ];
  } else {
    layers = [
      {
        typ: AnnotationLayerEnum.Skeleton,
        name: "Skeleton",
      },
      {
        typ: AnnotationLayerEnum.Volume,
        name: fallbackLayerName,
        fallbackLayerName,
        autoFallbackLayer,
        mappingName,
        magRestrictions,
      },
    ];
  }

  return Request.sendJSONReceiveJSON(url, { ...options, data: layers });
}

export async function getTracingsForAnnotation(
  annotation: APIAnnotation,
  version?: number | null | undefined,
): Promise<Array<ServerTracing>> {
  const skeletonLayers = annotation.annotationLayers.filter(
    (layer) => layer.typ === AnnotationLayerEnum.Skeleton,
  );
  const fullAnnotationLayers = await Promise.all(
    annotation.annotationLayers.map((layer) =>
      getTracingForAnnotationType(annotation, layer, version),
    ),
  );

  if (skeletonLayers.length > 1) {
    throw new Error(
      "Having more than one skeleton layer is currently not supported by WEBKNOSSOS.",
    );
  }

  return fullAnnotationLayers;
}

export async function acquireAnnotationMutex(
  annotationId: string,
  sessionId: string,
): Promise<{
  canEdit: boolean;
  // If the mutex could not be rejected, the following two properties contain
  // which user (and which session id) is responsible. The current user might
  // be blocking the mutex in another session (then, the session id will be
  // different from the current one).
  blockedByUser: APIUserCompact | undefined | null;
  blockedBySessionId: string | undefined | null;
}> {
  const { canEdit, blockedByUser, blockedBySessionId } = await Request.receiveJSON(
    `/api/annotations/${annotationId}/acquireMutex?${new URLSearchParams({ sessionId })}`,
    {
      method: "POST",
    },
  );
  return { canEdit, blockedByUser, blockedBySessionId };
}

export async function releaseAnnotationMutex(
  annotationId: string,
  sessionId: string,
): Promise<void> {
  await Request.receiveJSON(
    `/api/annotations/${annotationId}/releaseMutex?${new URLSearchParams({ sessionId })}`,
    {
      method: "POST",
    },
  );
}

export function releaseAnnotationMutexWithBeacon(annotationId: string, sessionId: string): boolean {
  return navigator.sendBeacon(
    `/api/annotations/${annotationId}/releaseMutex?${new URLSearchParams({ sessionId })}`,
  );
}

export async function getTracingForAnnotationType(
  annotation: APIAnnotation,
  annotationLayerDescriptor: AnnotationLayerDescriptor,
  version?: number | null | undefined,
): Promise<ServerTracing> {
  const { tracingId, typ } = annotationLayerDescriptor;
  const tracingType = typ.toLowerCase() as "skeleton" | "volume";
  const params = new URLSearchParams({ annotationId: annotation.id });
  if (version != null) {
    params.set("version", version.toString());
  }
  const tracingArrayBuffer = await doWithToken((token) => {
    params.set("token", token);
    return Request.receiveArraybuffer(
      `${annotation.tracingStore.url}/tracings/${tracingType}/${tracingId}?${params}`,
      {
        headers: {
          Accept: "application/x-protobuf",
        },
      },
    );
  });
  const tracing = parseProtoTracing(tracingArrayBuffer, tracingType);

  if (import.meta.env.MODE !== "test") {
    // Log to console as the decoded tracing is hard to inspect in the devtools otherwise.
    console.log(`Parsed protobuf ${tracingType} tracing:`, tracing);
  }
  // The tracing id is not contained in the server tracing, but in the annotation content.
  tracing.id = tracingId;
  // Additionally, we assign the typ property (skeleton vs volume).
  // Flow complains since we don't doublecheck that we assign the correct type depending
  // on the tracing's structure.
  tracing.typ = typ;

  // @ts-expect-error Remove datasetName and organizationId as these should not be used in the front-end, anymore.
  delete tracing.datasetName;
  // @ts-expect-error
  delete tracing.organizationId;

  return tracing;
}

export function getUpdateActionLog(
  tracingStoreUrl: string,
  annotationId: string,
  oldestVersion?: number,
  newestVersion?: number,
  truncateActionLog: boolean = false,
  sortAscending: boolean = false,
): Promise<Array<APIUpdateActionBatch>> {
  return doWithToken(async (token) => {
    const params = new URLSearchParams([["truncate", truncateActionLog.toString()]]);
    params.set("token", token);
    if (oldestVersion != null) {
      params.set("oldestVersion", oldestVersion.toString());
    }
    if (newestVersion != null) {
      params.set("newestVersion", newestVersion.toString());
    }
    const log: APIUpdateActionBatch[] = await retryAsyncFunction(() =>
      Request.receiveJSON(
        `${tracingStoreUrl}/tracings/annotation/${annotationId}/updateActionLog?${params}`,
      ),
    );

    if (sortAscending) {
      log.reverse();
    }
    return log;
  });
}

export function getNewestVersionForAnnotation(
  tracingStoreUrl: string,
  annotationId: string,
): Promise<number> {
  return retryAsyncFunction(() =>
    doWithToken((token) =>
      Request.receiveJSON(
        `${tracingStoreUrl}/tracings/annotation/${annotationId}/newestVersion?token=${token}`,
      ).then((obj) => obj.version),
    ),
  );
}

export async function getAnnotationProto(
  tracingStoreUrl: string,
  annotationId: string,
  version?: number | null | undefined,
): Promise<APITracingStoreAnnotation> {
  const params = new URLSearchParams();
  if (version != null) {
    params.set("version", version.toString());
  }
  const annotationArrayBuffer = await doWithToken((token) => {
    params.set("token", token);
    return Request.receiveArraybuffer(
      `${tracingStoreUrl}/tracings/annotation/${annotationId}?${params}`,
      {
        headers: {
          Accept: "application/x-protobuf",
        },
      },
    );
  });
  const annotationProto = parseProtoAnnotation(annotationArrayBuffer);
  if (import.meta.env.MODE !== "test") {
    // Log to console as the decoded annotationProto is hard to inspect in the devtools otherwise.
    console.log("Parsed protobuf annotation:", annotationProto);
  }
  return annotationProto;
}

export function hasSegmentIndexInDataStore(
  dataStoreUrl: string,
  datasetId: string,
  dataLayerName: string,
) {
  return doWithToken((token) =>
    Request.receiveJSON(
      `${dataStoreUrl}/data/datasets/${datasetId}/layers/${dataLayerName}/hasSegmentIndex?token=${token}`,
    ),
  );
}

export const hasSegmentIndexInDataStoreCached = memoize(hasSegmentIndexInDataStore, (...args) =>
  args.join("::"),
);

export function getSegmentVolumes(
  layerSourceInfo: LayerSourceInfo,
  mag: Vector3,
  segmentIds: Array<bigint>,
  additionalCoordinates: AdditionalCoordinate[] | undefined | null,
  mappingName: string | null | undefined,
  annotationVersion: number | undefined,
): Promise<number[]> {
  const requestUrl = getDataOrTracingStoreUrl(layerSourceInfo);
  return doWithToken((token) =>
    Request.sendJSONReceiveJSON(`${requestUrl}/segmentStatistics/volume?token=${token}`, {
      data: { additionalCoordinates, mag, segmentIds, mappingName, annotationVersion },
      method: "POST",
    }),
  );
}

type SegmentStatisticsParametersMeshBased = {
  mag: Vector3;
  segmentIds: bigint[];
  mappingName?: string | null;
  additionalCoordinates?: AdditionalCoordinate[] | null;
  meshFileName?: string | null;
  annotationVersion: number | undefined;
};

export function getSegmentSurfaceArea(
  layerSourceInfo: LayerSourceInfo,
  mag: Vector3,
  meshFileName: string | undefined | null,
  segmentIds: Array<bigint>,
  additionalCoordinates: AdditionalCoordinate[] | undefined | null,
  mappingName: string | null | undefined,
  annotationVersion: number | undefined,
): Promise<number[]> {
  const requestUrl = getDataOrTracingStoreUrl(layerSourceInfo);
  return doWithToken((token) => {
    const data: SegmentStatisticsParametersMeshBased = {
      mag,
      segmentIds,
      mappingName,
      additionalCoordinates,
      meshFileName,
      annotationVersion,
    };
    return Request.sendJSONReceiveJSON(
      `${requestUrl}/segmentStatistics/surfaceArea?token=${token}`,
      {
        data,
        method: "POST",
      },
    );
  });
}

export function getSegmentBoundingBoxes(
  layerSourceInfo: LayerSourceInfo,
  mag: Vector3,
  segmentIds: Array<bigint>,
  additionalCoordinates: AdditionalCoordinate[] | undefined | null,
  mappingName: string | null | undefined,
  annotationVersion: number | undefined,
): Promise<Array<{ topLeft: Vector3; width: number; height: number; depth: number }>> {
  const requestUrl = getDataOrTracingStoreUrl(layerSourceInfo);
  return doWithToken((token) =>
    Request.sendJSONReceiveJSON(`${requestUrl}/segmentStatistics/boundingBox?token=${token}`, {
      data: { additionalCoordinates, mag, segmentIds, mappingName, annotationVersion },
      method: "POST",
    }),
  );
}

/**
 * Reports the segment statistics attachment of a layer, if it has one. The backend answers with an
 * array of zero or one element, which is unwrapped here. Any error (e.g. a file with non-dense ids,
 * which the backend rejects) is treated as "no usable file".
 */
export async function getSegmentStatisticsFileInfo(
  dataStoreUrl: string,
  datasetId: string,
  dataLayerName: string,
): Promise<SegmentStatisticsFileInfo | null> {
  try {
    const infos: SegmentStatisticsFileInfo[] = await doWithToken((token) =>
      Request.receiveJSON(
        `${dataStoreUrl}/data/datasets/${datasetId}/layers/${dataLayerName}/segmentStatisticsFile?token=${token}`,
      ),
    );
    return infos[0] ?? null;
  } catch (_error) {
    return null;
  }
}

/**
 * The statistics-file-backed routes below exist only on the datastore, so they are always requested
 * from there – even for a layer that belongs to a volume annotation.
 */
function fetchSegmentStatistic<T>(
  layerSourceInfo: LayerSourceInfo,
  route: string,
  mag: Vector3,
  segmentIds: Array<bigint>,
  additionalCoordinates: AdditionalCoordinate[] | undefined | null,
  mappingName: string | null | undefined,
): Promise<T[]> {
  const requestUrl = getDataOrTracingStoreUrl({ ...layerSourceInfo, useDataStore: true });
  return doWithToken((token) =>
    Request.sendJSONReceiveJSON(`${requestUrl}/segmentStatistics/${route}?token=${token}`, {
      data: { additionalCoordinates, mag, segmentIds, mappingName },
      method: "POST",
    }),
  );
}

/** Longest distance within each segment, in the dataset unit. */
export function getSegmentMaxDistances(
  layerSourceInfo: LayerSourceInfo,
  mag: Vector3,
  segmentIds: Array<bigint>,
  additionalCoordinates: AdditionalCoordinate[] | undefined | null,
  mappingName: string | null | undefined,
): Promise<number[]> {
  return fetchSegmentStatistic<number>(
    layerSourceInfo,
    "maxDistance",
    mag,
    segmentIds,
    additionalCoordinates,
    mappingName,
  );
}

/** Dimensionless and scale-invariant, in [0, 1]. */
export function getSegmentSphericities(
  layerSourceInfo: LayerSourceInfo,
  mag: Vector3,
  segmentIds: Array<bigint>,
  additionalCoordinates: AdditionalCoordinate[] | undefined | null,
  mappingName: string | null | undefined,
): Promise<number[]> {
  return fetchSegmentStatistic<number>(
    layerSourceInfo,
    "sphericity",
    mag,
    segmentIds,
    additionalCoordinates,
    mappingName,
  );
}

/** Centers of mass in mag1 voxels (floats, not rounded to voxel positions). */
export function getSegmentCentersOfMass(
  layerSourceInfo: LayerSourceInfo,
  mag: Vector3,
  segmentIds: Array<bigint>,
  additionalCoordinates: AdditionalCoordinate[] | undefined | null,
  mappingName: string | null | undefined,
): Promise<Vector3[]> {
  return fetchSegmentStatistic<Vector3>(
    layerSourceInfo,
    "centerOfMass",
    mag,
    segmentIds,
    additionalCoordinates,
    mappingName,
  );
}

/** Row-major 3×3 covariance matrices in squared mag1 voxels. */
export function getSegmentCovarianceMatrices(
  layerSourceInfo: LayerSourceInfo,
  mag: Vector3,
  segmentIds: Array<bigint>,
  additionalCoordinates: AdditionalCoordinate[] | undefined | null,
  mappingName: string | null | undefined,
): Promise<SegmentCovarianceMatrix[]> {
  return fetchSegmentStatistic<SegmentCovarianceMatrix>(
    layerSourceInfo,
    "covarianceMatrix",
    mag,
    segmentIds,
    additionalCoordinates,
    mappingName,
  );
}

export async function importVolumeTracing(
  annotation: StoreAnnotation,
  volumeTracing: VolumeTracing,
  dataFile: File,
  version: number,
): Promise<bigint> {
  const largestSegmentId: bigint = await doWithToken((token) =>
    Request.sendMultipartFormReceiveJSON(
      `${annotation.tracingStore.url}/tracings/volume/${volumeTracing.tracingId}/importVolumeData?token=${token}`,
      {
        data: {
          dataFile,
          currentVersion: version,
        },
      },
    ),
  );
  return largestSegmentId;
}

export async function downloadWithFilename(downloadUrl: string) {
  const link = document.createElement("a");
  link.href = downloadUrl;
  link.rel = "noopener";
  // Without this, the browser treats the click as a top-level navigation until the
  // Content-Disposition header arrives, firing beforeunload and releasing the annotation
  // mutex. The download attribute marks it as a forced download upfront.
  link.download = "";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

export async function downloadAnnotation(
  annotationId: string,
  annotationType: APIAnnotationType,
  showVolumeFallbackDownloadWarning: boolean = false,
  version: number | null | undefined = null,
  downloadFileFormat: "zarr3" | "wkw" | "nml" = "wkw",
  includeVolumeData: boolean = true,
) {
  const params = new URLSearchParams();
  if (version != null) {
    params.set("version", version.toString());
  }

  if (includeVolumeData && showVolumeFallbackDownloadWarning) {
    Toast.info(messages["annotation.no_fallback_data_included"], {
      timeout: 12000,
    });
  }
  if (!includeVolumeData) {
    params.set("skipVolumeData", "true");
  } else {
    if (downloadFileFormat === "nml") {
      throw new Error(
        "Cannot download annotation with nml-only format while includeVolumeData is true",
      );
    }
    params.set("volumeDataZipFormat", downloadFileFormat);
  }

  const downloadUrl = `/api/annotations/${annotationType}/${annotationId}/download?${params}`;
  await downloadWithFilename(downloadUrl);
}

export async function getIdReservationsForAnnotation(
  annotationId: string,
  tracingId: string,
  domain: AnnotationIdDomain,
) {
  const params = new URLSearchParams({ tracingId, domain });

  const ids: number[] = await Request.receiveJSON(
    `/api/annotations/${annotationId}/reservedIds?${params}`,
  );
  return ids;
}

export async function reserveIdsForAnnotation(
  annotationId: string,
  tracingId: string,
  domain: AnnotationIdDomain,
  numberOfIdsToReserve: number,
  idsToRelease: number[] = [],
): Promise<number[]> {
  /*
   * Will reserve new ids for the specified domain.
   */
  if (numberOfIdsToReserve <= 0) {
    // Otherwise, the backend cannot reliably use the largest id
    // in a domain as a starting point to generate new ids.
    // For example:
    // User 1 has reservations for ids 1 and 2.
    // User 2 has reservations for ids 3 and 4 and releases
    // these (without reserving new ones).
    // Then, the known maximum is 2 (instead of 4).
    throw new Error("Must reserve at least 1 id");
  }
  if (numberOfIdsToReserve > Constants.IDEAL_ID_BUFFER_SIZE) {
    throw new Error(
      `Tried to request too many ids. numberOfIdsToReserve (${numberOfIdsToReserve} > ${Constants.IDEAL_ID_BUFFER_SIZE})`,
    );
  }
  const ids: number[] = await Request.sendJSONReceiveJSON(
    `/api/annotations/${annotationId}/reserveIds`,
    {
      data: {
        domain,
        tracingId,
        numberOfIdsToReserve,
        idsToRelease,
      },
      method: "POST",
    },
  );
  return ids;
}

// ### Datasets

export async function getDatasets(
  isUnreported: boolean | null | undefined = null,
  folderId: string | null = null,
  searchQuery: string | null = null,
  includeSubfolders: boolean | null = null,
  limit: number | null = null,
  includeAnnotationCount: boolean = false,
): Promise<Array<APIDatasetCompact>> {
  const params = new URLSearchParams();
  if (isUnreported != null) {
    params.set("isUnreported", String(isUnreported));
  }
  if (folderId != null && folderId !== "") {
    params.set("folderId", folderId);
  }
  if (searchQuery != null) {
    params.set("searchQuery", searchQuery.trim());
  }
  if (limit != null) {
    params.set("limit", String(limit));
  }
  if (includeSubfolders != null) {
    params.set("includeSubfolders", includeSubfolders ? "true" : "false");
  }
  if (includeAnnotationCount) {
    params.set("includeAnnotationCount", "true");
  }

  const datasets = await Request.receiveJSON(`/api/datasets?${params}`);
  assertResponseLimit(datasets);
  return datasets;
}

export async function getActiveDatasetsOfMyOrganization(): Promise<Array<APIDatasetCompact>> {
  const datasets: Array<APIDatasetCompact> = await Request.receiveJSON(
    "/api/datasets?isActive=true&onlyMyOrganization=true",
  );
  assertResponseLimit(datasets);
  return datasets;
}

export async function getDataset(
  datasetId: string,
  sharingToken?: string | null | undefined,
  options: RequestOptions = {},
  filterZeroMagLayers: boolean = true,
): Promise<APIMaybeUnimportedDataset> {
  const params = new URLSearchParams();
  if (sharingToken != null) {
    params.set("sharingToken", String(sharingToken));
  }

  const dataset: APIDataset = await Request.receiveJSON(
    `/api/datasets/${datasetId}?${params}`,
    options,
  );

  if (!filterZeroMagLayers || !("dataLayers" in (dataset.dataSource ?? {}))) {
    return dataset;
  }
  return update(dataset, {
    dataSource: {
      dataLayers: {
        $set: dataset.dataSource.dataLayers.filter((layer) => layer.mags.length > 0),
      },
    },
  });
}

export async function getImportedDataset(
  datasetId: string,
  sharingToken?: string | null | undefined,
  options: RequestOptions = {},
  filterZeroMagLayers: boolean = true,
): Promise<APIDataset> {
  const ds = await getDataset(datasetId, sharingToken, options, filterZeroMagLayers);
  if ("dataLayers" in ds.dataSource) {
    return ds as APIDataset;
  }
  throw new Error(`Dataset with id ${datasetId} is not imported.`);
}

export async function getDatasetLegacy(
  datasetOrga: string,
  datasetName: string,
  sharingToken?: string | null | undefined,
  options: RequestOptions = {},
): Promise<APIDataset> {
  const datasetId = await getDatasetIdFromNameAndOrganization(
    datasetName,
    datasetOrga,
    sharingToken,
    options,
  );
  return getImportedDataset(datasetId, sharingToken, options);
}

export type DatasetUpdater = {
  description?: string | null;
  displayName?: string | null;
  sortingKey?: number;
  isPublic?: boolean;
  tags?: string[];
  folderId?: string;
  metadata?: APIDataset["metadata"];
  dataSource?: APIDataSource;
};

export async function updateDatasetPartial(
  datasetId: string,
  updater: Partial<DatasetUpdater>,
  options: RequestOptions = {},
): Promise<APIDataset> {
  return Request.sendJSONReceiveJSON(`/api/datasets/${datasetId}/updatePartial`, {
    method: "PATCH",
    data: updater,
    ...options,
  });
}

export async function getDatasetViewConfiguration(
  dataset: APIDataset,
  displayedVolumeTracings: Array<string>,
  sharingToken?: string | null | undefined,
): Promise<DatasetConfiguration> {
  const sharingTokenSuffix = sharingToken != null ? `?sharingToken=${sharingToken}` : "";
  const settings = await Request.sendJSONReceiveJSON(
    `/api/datasetConfigurations/${dataset.id}${sharingTokenSuffix}`,
    {
      data: displayedVolumeTracings,
      method: "POST",
    },
  );
  enforceValidatedDatasetViewConfiguration(settings, dataset);
  return settings;
}

export function updateDatasetConfiguration(
  datasetId: string,
  datasetConfig: PartialDatasetConfiguration,
  options: RequestOptions = {},
): Promise<Record<string, any>> {
  return Request.sendJSONReceiveJSON(`/api/datasetConfigurations/${datasetId}`, {
    ...options,
    method: "PUT",
    data: datasetConfig,
  });
}

export function getDatasetDefaultConfiguration(datasetId: string): Promise<DatasetConfiguration> {
  return Request.receiveJSON(`/api/datasetConfigurations/default/${datasetId}`);
}

export function updateDatasetDefaultConfiguration(
  datasetId: string,
  datasetConfiguration: DatasetConfiguration,
  options?: RequestOptions,
): Promise<ArbitraryObject> {
  return Request.sendJSONReceiveJSON(`/api/datasetConfigurations/default/${datasetId}`, {
    method: "PUT",
    data: datasetConfiguration,
    ...options,
  });
}

export function getDatasetAccessList(dataset: APIMaybeUnimportedDataset): Promise<Array<APIUser>> {
  return Request.receiveJSON(`/api/datasets/${dataset.id}/accessList`);
}

type DatasetCompositionArgs = {
  newDatasetName: string;
  targetFolderId: string;
  organizationId: string;
  voxelSize: VoxelSize;
  layers: LayerLink[];
};

export function createDatasetComposition(
  payload: DatasetCompositionArgs,
): Promise<NewDatasetReply> {
  return doWithToken((token) =>
    Request.sendJSONReceiveJSON(`/api/datasets/compose?token=${token}`, {
      data: payload,
    }),
  );
}

export function createResumableUpload(
  datastoreUrl: string,
  uploadId: string,
): Promise<ResumableUpload> {
  // @ts-expect-error ts-migrate(7006) FIXME: Parameter 'file' implicitly has an 'any' type.
  const generateUniqueIdentifier = (file) => {
    if (file.path == null) {
      // file.path should be set by react-dropzone (which uses file-selector::toFileWithPath).
      // In case this "enrichment" of the file should change at some point (e.g., due to library changes),
      // throw an error.
      throw new Error("file.path is undefined.");
    }

    return `${uploadId}/${file.path || file.name}`;
  };

  return doWithToken(async (initialToken) => {
    let activeToken = initialToken;
    const handleInvalidToken = async () => {
      const newToken = await refreshToken();
      activeToken = newToken;
    };

    const resumable = new ResumableUpload({
      testChunks: true,
      target: `${datastoreUrl}/data/datasets/upload/dataset`,
      query: function () {
        return {
          token: activeToken,
        };
      },
      chunkSize: 10 * 1024 * 1024, // 10MB
      simultaneousUploads: 3,
      chunkRetryInterval: 2000,
      maxChunkRetries: undefined,
      fetchTimeout: 10 * 60 * 1000, // 10m
      generateUniqueIdentifier,
      // The following errors only tell Resumable-upload to not
      // retry automatically when they appear.
      // 403 is explicitly listed because we want to get a fileError
      // event. Then, we can invalidate the token and trigger
      // a retry ourselves (see below).
      permanentErrors: [400, 403, 404, 409, 415, 501],
    });

    let lastFileErrorTimestamp: number | null = null;
    resumable.addEventListener("fileError", (event: Event) => {
      const { file, message } = (event as CustomEvent).detail;
      // When a file could not be uploaded, assume that the token is invalid. Then,
      // refresh the token (unless we already did this in the last hour) and
      // retry the file upload.
      const ONE_HOUR_MS = 3600 * 1000;
      if (lastFileErrorTimestamp == null || Date.now() - lastFileErrorTimestamp > ONE_HOUR_MS) {
        handleInvalidToken()
          .then(() => {
            file.retry();
          })
          .catch(() => {
            // Note that "terminalFileError" is an event which is only triggered by WK
            // and not by the ResumableUpload library itself.
            resumable.dispatchFromDetail({ type: "terminalFileError", file, message });
          });
      } else {
        resumable.dispatchFromDetail({ type: "terminalFileError", file, message });
      }

      lastFileErrorTimestamp = Date.now();
    });

    return resumable;
  });
}

type ResumableUploadInfo = {
  uploadId: string;
  totalFileCount: number;
  filePaths: Array<string>;
  totalFileSizeInBytes: number;
};
type DatasetUploadInfo = {
  resumableUploadInfo: ResumableUploadInfo;
  datasetName: string;
  organizationId: string;
  layersToLink: Array<null>; // Always set as empty by frontend, only used by libs
  initialTeamIds: Array<string>;
  folderId: string | null;
  needsConversion: boolean;
  voxelSizeFactor: Vector3 | undefined;
  voxelSizeUnit: string | undefined;
};

export function reserveDatasetUpload(
  datastoreHost: string,
  datasetUploadInfo: DatasetUploadInfo,
): Promise<void> {
  return doWithToken((token) =>
    Request.sendJSONReceiveJSON(`/data/datasets/upload/dataset/reserveUpload?token=${token}`, {
      data: datasetUploadInfo,
      host: datastoreHost,
    }),
  );
}

export type UnfinishedUpload = {
  uploadId: string;
  datasetName: string;
  folderId: string;
  created: number;
  filePaths: Array<string> | null | undefined;
  allowedTeams: Array<string>;
};

export function getUnfinishedUploads(
  datastoreHost: string,
  organizationName: string,
): Promise<UnfinishedUpload[]> {
  return doWithToken(async (token) => {
    const unfinishedUploads = (await Request.receiveJSON(
      `/data/datasets/upload/dataset/unfinishedUploads?token=${token}&organizationName=${organizationName}`,
      {
        host: datastoreHost,
      },
    )) as Array<UnfinishedUpload>;
    return unfinishedUploads;
  });
}

type NewDatasetReply = {
  newDatasetId: string;
};

type FinishUploadReply = {
  datasetId: string;
};

export function finishDatasetUpload(
  datastoreHost: string,
  uploadId: string,
): Promise<FinishUploadReply> {
  return doWithToken((token) =>
    Request.receiveJSON(
      `/data/datasets/upload/dataset/finishUpload?uploadId=${uploadId}&token=${token}`,
      {
        host: datastoreHost,
        method: "POST",
      },
    ),
  );
}

export function cancelDatasetUpload(datastoreHost: string, uploadId: string): Promise<void> {
  return doWithToken((token) =>
    Request.receiveJSON(
      `/data/datasets/upload/dataset/cancelUpload?uploadId=${uploadId}&token=${token}`,
      {
        host: datastoreHost,
        method: "POST",
      },
    ),
  );
}

type ExplorationResult = {
  dataSource: DatasourceConfiguration | undefined;
  report: string;
};

export async function exploreRemoteDataset(
  remoteUris: string[],
  datastoreName: string,
  credentials?: { username: string; pass: string } | null,
  preferredVoxelSize?: Vector3,
): Promise<ExplorationResult> {
  const { dataSource, report } = await Request.sendJSONReceiveJSON("/api/datasets/exploreRemote", {
    data: remoteUris.map((uri) => {
      const extendedUri = {
        remoteUri: uri.trim(),
        preferredVoxelSize,
        datastoreName,
      };

      if (credentials) {
        return {
          ...extendedUri,
          credentialIdentifier: credentials.username,
          credentialSecret: credentials.pass,
        };
      }

      return extendedUri;
    }),
  });
  if (report.indexOf("403 Forbidden") !== -1 || report.indexOf("401 Unauthorized") !== -1) {
    Toast.error("The data could not be accessed. Please verify the credentials!");
  }
  return { dataSource, report };
}

export async function findDatasetByImportUrl(importUrl: string): Promise<APIDataset | null> {
  return await Request.receiveJSON(
    `/api/datasets/findByImportURL?importURL=${encodeURIComponent(importUrl)}`,
  );
}

type StoreRemoteDatasetArgs = {
  dataStoreName: string;
  dataSource: APIDataSource;
  folderId?: string | null;
  importUrl?: string | null;
};

export async function storeRemoteDataset(
  dataStoreName: string,
  datasetName: string,
  dataSource: APIDataSource,
  importUrl: string | null | undefined,
  folderId: string | null,
): Promise<NewDatasetReply> {
  const payload: StoreRemoteDatasetArgs = {
    dataSource,
    dataStoreName,
  };
  if (importUrl) {
    payload["importUrl"] = importUrl;
  }
  if (folderId) {
    payload["folderId"] = folderId;
  }

  return Request.sendJSONReceiveJSON(`/api/datasets/addVirtualDataset/${datasetName}`, {
    method: "POST",
    data: payload,
  });
}

// Returns void if the name is valid. Otherwise, a string is returned which denotes the reason.
export async function isDatasetNameValid(datasetName: string): Promise<string | null | undefined> {
  if (datasetName === "") {
    return "The dataset name must not be empty.";
  }

  const response = await Request.receiveJSON(`/api/datasets/${datasetName}/isValidNewName`);
  if (response.isValid) {
    return null;
  } else {
    return response.errors[0];
  }
}

export async function updateDatasetTeams(
  datasetId: string,
  newTeams: Array<string>,
  options: RequestOptions = {},
): Promise<APIDataset> {
  return Request.sendJSONReceiveJSON(`/api/datasets/${datasetId}/teams`, {
    method: "PATCH",
    data: newTeams,
    ...options,
  });
}

export function getDatasetUsedStorageDetails(datasetId: string): Promise<APIStorageDetailEntry[]> {
  return Request.receiveJSON(`/api/datasets/${datasetId}/usedStorageDetails`);
}

export async function triggerDatasetCheck(
  datastoreHost: string,
  organizationId?: string,
): Promise<void> {
  await doWithToken((token) => {
    const params = new URLSearchParams();
    params.set("token", token);
    if (organizationId) {
      params.set("organizationId", organizationId);
    }
    return Request.triggerRequest(`/data/triggers/checkInboxBlocking?${params}`, {
      host: datastoreHost,
      method: "POST",
    });
  });
}

async function triggerDatasetClearCache(
  datastoreHost: string,
  dataSourceId: APIDataSourceId,
  datasetId: string,
  layerName?: string,
): Promise<void> {
  await doWithToken((token) => {
    const params = new URLSearchParams();
    params.set("token", token);
    if (layerName) {
      params.set("layerName", layerName);
    }
    return Request.triggerRequest(
      `/data/triggers/reload/${dataSourceId.owningOrganization}/${datasetId}?${params}`,
      {
        host: datastoreHost,
        method: "POST",
      },
    );
  });
}

export async function deleteDatasetOnDisk(datasetId: string): Promise<void> {
  await Request.triggerRequest(`/api/datasets/${datasetId}`, {
    method: "DELETE",
  });
}

async function triggerDatasetClearThumbnailCache(datasetId: string): Promise<void> {
  await Request.triggerRequest(`/api/datasets/${datasetId}/clearThumbnailCache`, {
    method: "PUT",
  });
}

export async function clearCache(dataset: APIMaybeUnimportedDataset, layerName?: string) {
  return Promise.all([
    triggerDatasetClearCache(dataset.dataStore.url, dataset, dataset.id, layerName),
    triggerDatasetClearThumbnailCache(dataset.id),
  ]);
}

export async function getDatasetSharingToken(
  datasetId: string,
  options?: RequestOptions,
): Promise<string> {
  const { sharingToken } = await Request.receiveJSON(
    `/api/datasets/${datasetId}/sharingToken`,
    options,
  );
  return sharingToken;
}

export async function revokeDatasetSharingToken(datasetId: string): Promise<void> {
  await Request.triggerRequest(`/api/datasets/${datasetId}/sharingToken`, {
    method: "DELETE",
  });
}

export async function findDataPositionForLayer(
  datastoreUrl: string,
  dataset: APIDataset,
  layerName: string,
): Promise<{
  position: Vector3 | null | undefined;
  mag: Vector3 | null | undefined;
}> {
  const { position, mag } = await doWithToken((token) =>
    Request.receiveJSON(
      `${datastoreUrl}/data/datasets/${dataset.id}/layers/${layerName}/findData?token=${token}`,
    ),
  );
  return {
    position,
    mag,
  };
}

export async function findDataPositionForVolumeTracing(
  tracingstoreUrl: string,
  tracingId: string,
): Promise<{
  position: Vector3 | null | undefined;
  mag: Vector3 | null | undefined;
}> {
  const { position, mag } = await doWithToken((token) =>
    Request.receiveJSON(`${tracingstoreUrl}/tracings/volume/${tracingId}/findData?token=${token}`),
  );
  return {
    position,
    mag,
  };
}

export async function getHistogramForLayer(
  datastoreUrl: string,
  datasetId: string,
  layerName: string,
): Promise<APIHistogramData> {
  return doWithToken((token) =>
    Request.receiveJSON(
      `${datastoreUrl}/data/datasets/${datasetId}/layers/${layerName}/histogram?token=${token}`,
      { showErrorToast: false },
    ),
  );
}

export async function getMappingsForDatasetLayer(
  datastoreUrl: string,
  dataset: APIDataset,
  layerName: string,
  options: RequestOptions = {},
  retryOptions?: RetryOptions,
): Promise<ApiResult<Array<string>>> {
  return requestResult(
    (adaptedOptions) =>
      doWithToken((token) =>
        Request.receiveJSON(
          `${datastoreUrl}/data/datasets/${dataset.id}/layers/${layerName}/mappings?token=${token}`,
          adaptedOptions,
        ),
      ),
    options,
    retryOptions,
  );
}

export async function fetchMapping(
  datastoreUrl: string,
  dataset: APIDataset,
  layerName: string,
  mappingName: string,
  options: RequestOptions = {},
  retryOptions?: RetryOptions,
): Promise<ApiResult<APIMapping>> {
  return requestResult(
    (adaptedOptions) =>
      doWithToken((token) =>
        Request.receiveJSON(
          `${datastoreUrl}/data/datasets/${dataset.id}/layers/${layerName}/mappings/${mappingName}?token=${token}`,
          adaptedOptions,
        ),
      ),
    options,
    retryOptions,
  );
}

export async function getEditableMappingInfo(
  tracingStoreUrl: string,
  tracingId: string,
  annotationId: string,
  version: number | undefined | null,
  options: RequestOptions = {},
  retryOptions?: RetryOptions,
): Promise<ApiResult<ServerEditableMapping>> {
  return requestResult(
    (adaptedOptions) =>
      doWithToken((token) => {
        const params = new URLSearchParams({
          token,
          annotationId: `${annotationId}`,
        });
        if (version != null) {
          params.set("version", version.toString());
        }
        return Request.receiveJSON(
          `${tracingStoreUrl}/tracings/mapping/${tracingId}/info?${params}`,
          adaptedOptions,
        );
      }),
    options,
    retryOptions,
  );
}

export function getPositionForSegmentInAgglomerate(
  datastoreUrl: string,
  datasetId: string,
  layerName: string,
  mappingName: string,
  segmentId: bigint,
): Promise<Vector3> {
  return doWithToken(async (token) => {
    const params = new URLSearchParams({
      token,
      segmentId: `${segmentId}`,
    });
    const position = await Request.receiveJSON(
      `${datastoreUrl}/data/datasets/${datasetId}/layers/${layerName}/agglomerates/${mappingName}/positionForSegment?${params.toString()}`,
    );
    return position;
  });
}

export async function getAgglomeratesForDatasetLayer(
  datastoreUrl: string,
  dataset: APIDataset,
  layerName: string,
  options: RequestOptions = {},
  retryOptions?: RetryOptions,
): Promise<ApiResult<Array<string>>> {
  return requestResult(
    (adaptedOptions) =>
      doWithToken((token) =>
        Request.receiveJSON(
          `${datastoreUrl}/data/datasets/${dataset.id}/layers/${layerName}/agglomerates?token=${token}`,
          adaptedOptions,
        ),
      ),
    options,
    retryOptions,
  );
}

// #### Publications
export async function getPublications(): Promise<Array<APIPublication>> {
  const publications = await Request.receiveJSON("/api/publications");
  assertResponseLimit(publications);
  return publications;
}

export async function getPublication(id: string): Promise<APIPublication> {
  const publication = await Request.receiveJSON(`/api/publications/${id}`);
  return publication;
}

// #### Datastores
export async function getDatastores(): Promise<APIDataStore[]> {
  const datastores = await Request.receiveJSON("/api/datastores");
  assertResponseLimit(datastores);
  return datastores;
}

export const getDataStoresCached = memoize(getDatastores);

function getTracingstore(): Promise<APITracingStore> {
  return Request.receiveJSON("/api/tracingstore");
}

export const getTracingStoreCached = memoize(getTracingstore);

// ### Active User
export function getActiveUser(options?: RequestOptions): Promise<APIUser> {
  return Request.receiveJSON("/api/user", options);
}

export function getUserConfiguration(): Promise<UserConfiguration> {
  return Request.receiveJSON("/api/user/userConfiguration");
}

export function updateUserConfiguration(
  userConfiguration: Record<string, any>,
): Promise<Record<string, any>> {
  return Request.sendJSONReceiveJSON("/api/user/userConfiguration", {
    method: "PUT",
    data: userConfiguration,
  });
}

export function getKeyboardShortcutsConfig(): Promise<Partial<KeyboardShortcutsMap> | EmptyObject> {
  return Request.receiveJSON("/api/user/keyboardShortcutsConfig");
}

export function updateKeyboardShortcutsConfig(shortcuts: KeyboardShortcutsMap): Promise<void> {
  return Request.sendJSONReceiveJSON("/api/user/keyboardShortcutsConfig", {
    method: "PUT",
    data: shortcuts,
  });
}

export async function getTimeTrackingForUserSummedPerAnnotation(
  userId: string,
  startDate: dayjs.Dayjs,
  endDate: dayjs.Dayjs,
  annotationTypes: "Explorational" | "Task" | "Task,Explorational",
  annotationState: AnnotationStateFilterEnum,
  projectIds?: string[] | null,
): Promise<Array<APITimeTrackingPerAnnotation>> {
  const params = new URLSearchParams({
    start: startDate.valueOf().toString(),
    end: endDate.valueOf().toString(),
  });
  if (annotationTypes != null) params.set("annotationTypes", annotationTypes);
  if (projectIds != null && projectIds.length > 0) params.set("projectIds", projectIds.join(","));
  if (annotationState !== AnnotationStateFilterEnum.ALL) {
    params.set("annotationStates", annotationState);
  } else {
    params.set("annotationStates", "Active,Finished");
  }
  const timeTrackingData = await Request.receiveJSON(
    `/api/time/user/${userId}/summedByAnnotation?${params}`,
  );
  assertResponseLimit(timeTrackingData);
  return timeTrackingData;
}

export async function getTimeTrackingForUserSpans(
  userId: string,
  startDate: number,
  endDate: number,
  annotationTypes: "Explorational" | "Task" | "Task,Explorational",
  selectedState: AnnotationStateFilterEnum,
  projectIds?: string[] | null,
): Promise<Array<APITimeTrackingSpan>> {
  const params = new URLSearchParams({
    start: startDate.toString(),
    end: endDate.toString(),
  });
  if (annotationTypes != null) params.set("annotationTypes", annotationTypes);
  if (projectIds != null && projectIds.length > 0) {
    params.set("projectIds", projectIds.join(","));
  }
  if (selectedState !== AnnotationStateFilterEnum.ALL) {
    params.set("annotationStates", selectedState);
  } else {
    params.set("annotationStates", "Active,Finished");
  }
  return await Request.receiveJSON(`/api/time/user/${userId}/spans?${params}`);
}

export async function getTimeEntries(
  startMs: number,
  endMs: number,
  teamIds: string[],
  selectedTypes: AnnotationTypeFilterEnum,
  selectedState: AnnotationStateFilterEnum,
  projectIds: string[],
): Promise<Array<APITimeTrackingPerUser>> {
  const params = new URLSearchParams({
    start: startMs.toString(),
    end: endMs.toString(),
    annotationTypes: selectedTypes,
  });
  if (selectedState !== AnnotationStateFilterEnum.ALL) {
    params.set("annotationStates", selectedState);
  } else {
    params.set("annotationStates", "Active,Finished");
  }
  // Omit empty parameters in request
  if (projectIds.length > 0) params.set("projectIds", projectIds.join(","));
  if (teamIds.length > 0) params.set("teamIds", teamIds.join(","));
  return await Request.receiveJSON(`api/time/overview?${params}`);
}

export async function getProjectProgressReport(
  teamId: string,
  showErrorToast: boolean = true,
): Promise<Array<APIProjectProgressReport>> {
  const progressData = await Request.receiveJSON(`/api/teams/${teamId}/projectProgressReport`, {
    showErrorToast,
  });
  assertResponseLimit(progressData);
  return progressData;
}

export async function getAvailableTasksReport(
  teamId: string,
): Promise<Array<APIAvailableTasksReport>> {
  const availableTasksData = await Request.receiveJSON(`/api/teams/${teamId}/availableTasksReport`);
  assertResponseLimit(availableTasksData);
  return availableTasksData;
}

// ### Health
export function pingHealthEndpoint(url: string, path: "tracings" | "data"): Promise<void> {
  const healthEndpoint = `${url}/${path}/health`;
  return Request.triggerRequest(healthEndpoint, {
    doNotInvestigate: true,
    mode: "cors",
    timeout: 5000,
  });
}

// ### BuildInfo webknossos
export function getBuildInfo(): Promise<APIBuildInfoWk> {
  return Request.receiveJSON("/api/buildinfo", {
    doNotInvestigate: true,
    mode: "cors",
  });
}

// ### Feature Selection
export function getFeatureToggles(): Promise<APIFeatureToggles> {
  return Request.receiveJSON("/api/features");
}

export function getOperatorData(): Promise<string> {
  return Request.receiveJSON("/api/operatorData");
}

// ## Experience Domains
export function getExistingExperienceDomains(): Promise<ExperienceDomainList> {
  return Request.receiveJSON("/api/tasks/experienceDomains");
}

export async function isInMaintenance(): Promise<boolean> {
  const allMaintenances: Array<MaintenanceInfo> = await listCurrentAndUpcomingMaintenances();
  const currentEpoch = Date.now();
  const currentMaintenance = allMaintenances.find(
    (maintenance) => maintenance.startTime < currentEpoch,
  );
  return currentMaintenance != null;
}

export async function listCurrentAndUpcomingMaintenances(): Promise<Array<MaintenanceInfo>> {
  return Request.receiveJSON("/api/maintenances/listCurrentAndUpcoming");
}

function setMaintenance(bool: boolean): Promise<void> {
  return Request.triggerRequest("/api/maintenance", {
    method: bool ? "POST" : "DELETE",
  });
}
// @ts-expect-error
window.setMaintenance = setMaintenance;

// Meshes

// These parameters are bundled into an object to avoid that the computeAdHocMesh function
// receives too many parameters, since this doesn't play well with the saga typings.
type MeshRequest = {
  // The position is in voxels in mag 1
  positionWithPadding: Vector3;
  additionalCoordinates: AdditionalCoordinate[] | undefined;
  mag: Vector3;
  segmentId: bigint; // Segment to build mesh for
  // The cubeSize is in voxels in mag <mag>
  cubeSize: Vector3;
  scaleFactor: Vector3;
  mappingName: string | null | undefined;
  mappingType: MappingType | null | undefined;
  findNeighbors: boolean;
  annotationVersion: number;
};

export function computeAdHocMesh(
  layerSourceInfo: LayerSourceInfo,
  meshRequest: MeshRequest,
): Promise<{
  buffer: ArrayBuffer;
  neighbors: Array<number>;
}> {
  const requestUrl = getDataOrTracingStoreUrl(layerSourceInfo);
  const {
    positionWithPadding,
    additionalCoordinates,
    cubeSize,
    mappingName,
    scaleFactor,
    mag,
    ...rest
  } = meshRequest;

  return doWithToken(async (token) => {
    const params = new URLSearchParams();
    params.set("token", token);

    const { buffer, headers } = await Request.sendJSONReceiveArraybufferWithHeaders(
      `${requestUrl}/adHocMesh?${params}`,
      {
        data: {
          // The back-end needs a small padding at the border of the
          // bounding box to calculate the mesh. This padding
          // is added here to the position and bbox size.
          position: positionWithPadding, // position is in mag1
          additionalCoordinates,
          cubeSize, // cubeSize is in target mag
          // Name and type of mapping to apply before building mesh (optional)
          mapping: mappingName,
          voxelSizeFactorInUnit: scaleFactor,
          mag,
          ...rest,
        },
      },
    );
    const neighbors = (parseMaybe(headers.neighbors) as number[] | null) || [];
    return {
      buffer,
      neighbors,
    };
  });
}

export function getBucketPositionsForAdHocMesh(
  layerSourceInfo: LayerSourceInfo,
  segmentId: bigint,
  cubeSize: Vector3,
  mag: Vector3,
  additionalCoordinates: AdditionalCoordinate[] | null | undefined,
  mappingName: string | null | undefined,
  annotationVersion: number,
): Promise<Vector3[]> {
  const requestUrl = getDataOrTracingStoreUrl(layerSourceInfo);
  return doWithToken(async (token) => {
    const params = new URLSearchParams();
    params.set("token", token);
    const positions = await Request.sendJSONReceiveJSON(
      `${requestUrl}/segmentIndex/${segmentId}?${params}`,
      {
        data: {
          cubeSize,
          mag,
          additionalCoordinates,
          mappingName,
          annotationVersion,
        },
        method: "POST",
      },
    );
    return positions;
  });
}

export function getAgglomerateTreeAsSkeletonTracing(
  dataStoreUrl: string,
  dataset: APIDataset,
  layerName: string,
  mappingId: string,
  agglomerateId: bigint,
): Promise<ArrayBuffer> {
  return doWithToken((token) =>
    Request.receiveArraybuffer(
      `${dataStoreUrl}/data/datasets/${dataset.id}/layers/${layerName}/agglomerates/${mappingId}/tree/${agglomerateId}?token=${token}`, // The webworker code cannot do proper error handling and always expects an array buffer from the server.
      // The webworker code cannot do proper error handling and always expects an array buffer from the server.
      // However, the server might send an error json instead of an array buffer. Therefore, don't use the webworker code.
      {
        useWebworkerForArrayBuffer: false,
        showErrorToast: false,
      },
    ),
  );
}

async function _getAgglomeratesForSegmentsHelper<T extends number | bigint>(
  segmentIds: Set<T>,
  url: string,
  extraParams: URLSearchParams,
): Promise<Mapping> {
  // Segment id 0 represents unlabeled/background voxels and is never a real segment. It must
  // never be requested from the server or end up as a 0 -> 0 entry in the resulting mapping: 0 is
  // reserved as the "empty" sentinel in the GPU-side cuckoo table and is rejected as a key there.
  const filteredSegmentIds = new Set([...segmentIds].filter((id) => id !== 0 && id !== 0n));
  if (filteredSegmentIds.size === 0) {
    return new Map();
  }
  const sortedSegmentIdArray = [...filteredSegmentIds].sort((a, b) => (a < b ? -1 : a > b ? 1 : 0));
  const segmentIdBuffer = serializeProtoListOfLong(sortedSegmentIdArray.map(toBigInt));
  const listArrayBuffer = await doWithToken((token) => {
    const params = new URLSearchParams(extraParams);
    params.set("token", token);
    return retryAsyncFunction(() =>
      Request.receiveArraybuffer(`${url}?${params}`, {
        method: "POST",
        body: segmentIdBuffer,
        headers: { "Content-Type": "application/octet-stream" },
        showErrorToast: false,
      }),
    );
  });

  // Ensure that the values are bigint if the keys are bigint
  const adaptToType = getAdaptToTypeFunctionFromList(sortedSegmentIdArray);
  const mappedIds = parseProtoListOfLong(listArrayBuffer).map(adaptToType);

  if (sortedSegmentIdArray.length !== mappedIds.length) {
    throw new Error(
      "Unexpected mismatch of keys and values while mapping segment ids using tracing store.",
    );
  }

  const keyValues = zip(sortedSegmentIdArray, mappedIds);
  // @ts-expect-error
  return new Map(keyValues);
}

export function getAgglomeratesForSegmentsFromDatastore<T extends number | bigint>(
  dataStoreUrl: string,
  dataset: APIDataset,
  layerName: string,
  mappingId: string,
  segmentIds: Set<T>,
): Promise<Mapping> {
  return _getAgglomeratesForSegmentsHelper(
    segmentIds,
    `${dataStoreUrl}/data/datasets/${dataset.id}/layers/${layerName}/agglomerates/${mappingId}/agglomeratesForSegments`,
    new URLSearchParams(),
  );
}

export function getAgglomeratesForSegmentsFromTracingstore<T extends number | bigint>(
  tracingStoreUrl: string,
  tracingId: string,
  segmentIds: Set<T>,
  annotationId: string,
  version: number,
): Promise<Mapping> {
  const extraParams = new URLSearchParams({ annotationId });
  if (version != null) {
    extraParams.set("version", version.toString());
  }
  return _getAgglomeratesForSegmentsHelper(
    segmentIds,
    `${tracingStoreUrl}/tracings/mapping/${tracingId}/agglomeratesForSegments`,
    extraParams,
  );
}

export function getEditableAgglomerateTreeAsSkeletonTracing(
  tracingStoreUrl: string,
  tracingId: string,
  agglomerateId: bigint,
  version: number,
): Promise<ArrayBuffer> {
  return doWithToken((token) => {
    const params = new URLSearchParams({ token, version: version.toString() });
    return Request.receiveArraybuffer(
      `${tracingStoreUrl}/tracings/mapping/${tracingId}/agglomerateTree/${agglomerateId}?${params}`,
      // The webworker code cannot do proper error handling and always expects an array buffer from the server.
      // However, the server might send an error json instead of an array buffer. Therefore, don't use the webworker code.
      {
        useWebworkerForArrayBuffer: false,
        showErrorToast: false,
      },
    );
  });
}

export async function getMeshFilesForDatasetLayer(
  dataStoreUrl: string,
  dataset: APIDataset,
  layerName: string,
): Promise<Array<APIMeshFileInfo>> {
  const meshFiles: Array<APIMeshFileInfo> = await doWithToken((token) =>
    Request.receiveJSON(
      `${dataStoreUrl}/data/datasets/${dataset.id}/layers/${layerName}/meshes?token=${token}`,
    ),
  );

  for (const file of meshFiles) {
    if (file.mappingName === "") {
      file.mappingName = undefined;
    }
  }

  return meshFiles;
}

// ### Connectomes
export function getConnectomeFilesForDatasetLayer(
  dataStoreUrl: string,
  dataset: APIDataset,
  layerName: string,
): Promise<Array<APIConnectomeFile>> {
  return doWithToken((token) =>
    Request.receiveJSON(
      `${dataStoreUrl}/data/datasets/${dataset.id}/layers/${layerName}/connectomes?token=${token}`,
    ),
  );
}

export function getSynapsesOfAgglomerates(
  dataStoreUrl: string,
  dataset: APIDataset,
  layerName: string,
  connectomeFile: string,
  agglomerateIds: Array<bigint>,
): Promise<
  Array<{
    in: Array<number>;
    out: Array<number>;
  }>
> {
  return doWithToken((token) =>
    Request.sendJSONReceiveJSON(
      `${dataStoreUrl}/data/datasets/${dataset.id}/layers/${layerName}/connectomes/synapses?token=${token}`,
      {
        data: {
          connectomeFile,
          agglomerateIds,
        },
      },
    ),
  );
}

async function getSynapseSourcesOrDestinations(
  dataStoreUrl: string,
  dataset: APIDataset,
  layerName: string,
  connectomeFile: string,
  synapseIds: Array<number>,
  srcOrDst: "src" | "dst",
): Promise<Array<bigint>> {
  return doWithToken((token) =>
    Request.sendJSONReceiveJSON(
      `${dataStoreUrl}/data/datasets/${dataset.id}/layers/${layerName}/connectomes/synapses/${srcOrDst}?token=${token}`,
      {
        data: {
          connectomeFile,
          synapseIds,
        },
      },
    ),
  );
}

export function getSynapseSources(...args: any): Promise<Array<bigint>> {
  // @ts-expect-error ts-migrate(2556) FIXME: Expected 6 arguments, but got 1 or more.
  return getSynapseSourcesOrDestinations(...args, "src");
}

export function getSynapseDestinations(...args: any): Promise<Array<bigint>> {
  // @ts-expect-error ts-migrate(2556) FIXME: Expected 6 arguments, but got 1 or more.
  return getSynapseSourcesOrDestinations(...args, "dst");
}

export function getSynapsePositions(
  dataStoreUrl: string,
  dataset: APIDataset,
  layerName: string,
  connectomeFile: string,
  synapseIds: Array<number>,
): Promise<Array<Vector3>> {
  return doWithToken((token) =>
    Request.sendJSONReceiveJSON(
      `${dataStoreUrl}/data/datasets/${dataset.id}/layers/${layerName}/connectomes/synapses/positions?token=${token}`,
      {
        data: {
          connectomeFile,
          synapseIds,
        },
      },
    ),
  );
}

export function getSynapseTypes(
  dataStoreUrl: string,
  dataset: APIDataset,
  layerName: string,
  connectomeFile: string,
  synapseIds: Array<number>,
): Promise<{
  synapseTypes: Array<number>;
  typeToString: Array<string>;
}> {
  return doWithToken((token) =>
    Request.sendJSONReceiveJSON(
      `${dataStoreUrl}/data/datasets/${dataset.id}/layers/${layerName}/connectomes/synapses/types?token=${token}`,
      {
        data: {
          connectomeFile,
          synapseIds,
        },
      },
    ),
  );
}

export type MinCutTargetEdge = {
  position1: Vector3;
  position2: Vector3;
  segmentId1: bigint;
  segmentId2: bigint;
};
export async function getEdgesForAgglomerateMinCut(
  tracingStoreUrl: string,
  tracingId: string,
  version: number,
  segmentsInfo: {
    partition1: bigint[];
    partition2: bigint[];
    mag: Vector3;
    agglomerateId: bigint;
    editableMappingId: string;
  },
): Promise<Array<MinCutTargetEdge>> {
  return doWithToken((token) =>
    retryAsyncFunction(() =>
      Request.sendJSONReceiveJSON(
        `${tracingStoreUrl}/tracings/mapping/${tracingId}/agglomerateGraphMinCut?token=${token}`,
        {
          data: {
            // For a normal min-cut, the id at which the proofreading marker is at
            // will be put into partition1. The right-clicked segment/mesh will be
            // in partition2.
            ...segmentsInfo,
            version,
          },
        },
      ),
    ),
  );
}

export type NeighborInfo = {
  segmentId: bigint;
  neighbors: Array<{ segmentId: bigint; position: Vector3 }>;
};

export async function getNeighborsForAgglomerateNode(
  tracingStoreUrl: string,
  tracingId: string,
  version: number,
  segmentInfo: {
    segmentId: bigint;
    mag: Vector3;
    agglomerateId: bigint;
    editableMappingId: string;
  },
): Promise<NeighborInfo> {
  return doWithToken((token) =>
    retryAsyncFunction(() =>
      Request.sendJSONReceiveJSON(
        `${tracingStoreUrl}/tracings/mapping/${tracingId}/agglomerateGraphNeighbors?token=${token}`,
        {
          data: {
            version,
            ...segmentInfo,
          },
        },
      ),
    ),
  );
}

// ### Smart Select

export async function getSamMask(
  dataset: APIDataset,
  layerName: string,
  mag: Vector3,
  surroundingBoxMag1: BoundingBox, // in mag 1
  prompt:
    | {
        type: "BOUNDING_BOX"; // relative to topleft
        selectionTopLeftX: number; // int, in target mag
        selectionTopLeftY: number; // int, in target mag
        selectionBottomRightX: number; // int, in target mag
        selectionBottomRightY: number; // int, in target mag
      }
    | {
        type: "POINT";
        pointX: number; // int, relative to topleft
        pointY: number; // int, relative to topleft
      },
  additionalCoordinates: AdditionalCoordinate[],
  intensityRange?: readonly [number, number] | null,
): Promise<Uint8Array> {
  const params = new URLSearchParams();
  if (intensityRange != null) {
    params.set("intensityMin", `${intensityRange[0]}`);
    params.set("intensityMax", `${intensityRange[1]}`);
  }

  const { type: interactionType, ...promptWithoutType } = prompt;

  const buffer = await Request.sendJSONReceiveArraybuffer(
    `/api/datasets/${dataset.id}/layers/${layerName}/segmentAnythingMask?${params}`,
    {
      data: {
        mag,
        surroundingBoundingBox: surroundingBoxMag1.asServerBoundingBox(),
        additionalCoordinates,
        interactionType,
        ...promptWithoutType,
      },
      showErrorToast: false,
    },
  );
  return new Uint8Array(buffer);
}

// ### Short links
export const createShortLink = memoize(
  (longLink: string): Promise<ShortLink> =>
    Request.sendJSONReceiveJSON("/api/shortLinks", {
      method: "POST",
      // stringify is necessary because the back-end expects a JSON string
      // (i.e., a string which contains quotes at the beginning and end).
      // The Request module does not add additional string quotes
      // if the data parameter is already a string.
      data: JSON.stringify(longLink),
    }),
);

export function getShortLink(key: string): Promise<ShortLink> {
  return Request.receiveJSON(`/api/shortLinks/byKey/${key}`);
}

// ### Voxelytics
export async function getVoxelyticsWorkflows(): Promise<Array<VoxelyticsWorkflowListing>> {
  return Request.receiveJSON("/api/voxelytics/workflows");
}

export function getVoxelyticsWorkflow(
  workflowHash: string,
  runId: string | null,
): Promise<VoxelyticsWorkflowReport> {
  const params = new URLSearchParams();
  if (runId != null) {
    params.set("runId", runId);
  }
  return Request.receiveJSON(`/api/voxelytics/workflows/${workflowHash}?${params}`);
}

export function getVoxelyticsLogs(
  runId: string,
  taskName: string | null,
  minLevel: LOG_LEVELS,
  startTime: Date,
  endTime: Date,
  limit: number | null = null,
): Promise<Array<VoxelyticsLogLine>> {
  // Data is fetched with the limit from the end backward, i.e. the latest data is fetched first.
  // The data is still ordered chronologically, i.e. ascending timestamps.
  const params = new URLSearchParams({
    runId,
    minLevel,
    startTimestamp: startTime.getTime().toString(),
    endTimestamp: endTime.getTime().toString(),
  });
  if (taskName != null) {
    params.set("taskName", taskName);
  }
  if (limit != null) {
    params.set("limit", limit.toString());
  }
  return Request.receiveJSON(`/api/voxelytics/logs?${params}`);
}

export function getVoxelyticsChunkStatistics(
  workflowHash: string,
  runId: string | null,
  taskName: string,
): Promise<Array<VoxelyticsChunkStatistics>> {
  const params = new URLSearchParams({
    taskName,
  });
  if (runId != null) {
    params.set("runId", runId);
  }
  return Request.receiveJSON(`/api/voxelytics/workflows/${workflowHash}/chunkStatistics?${params}`);
}
export function getVoxelyticsArtifactChecksums(
  workflowHash: string,
  runId: string | null,
  taskName: string,
  artifactName?: string,
): Promise<Array<Record<string, string | number>>> {
  const params = new URLSearchParams({
    taskName,
  });
  if (runId != null) {
    params.set("runId", runId);
  }
  if (artifactName != null) {
    params.set("artifactName", artifactName);
  }
  return Request.receiveJSON(
    `/api/voxelytics/workflows/${workflowHash}/artifactChecksums?${params}`,
  );
}

export function deleteWorkflow(workflowHash: string): Promise<void> {
  return Request.triggerRequest(`/api/voxelytics/workflows/${workflowHash}`, {
    method: "DELETE",
  });
}

// ### Help / Feedback userEmail
export function sendHelpEmail(message: string) {
  return Request.sendJSONReceiveJSON(`/api/helpEmail`, {
    method: "POST",
    data: { message, currentUrl: window.location.href },
  });
}

export function requestSingleSignOnLogin() {
  return Request.receiveJSON("/api/auth/oidc/login");
}

export function verifyEmail(key: string) {
  return Request.receiveJSON(`/api/verifyEmail/${key}`, {
    method: "POST",
    showErrorToast: false,
  });
}

export function requestVerificationMail() {
  return Request.receiveJSON("/api/verifyEmail", {
    method: "POST",
  });
}

export function sendSaveRequestWithToken(
  urlWithoutToken: string,
  data: RequestOptionsWithData<Array<SaveQueueEntry>>,
): Promise<void> {
  // Ideally, this function should be refactored further so that it generates the
  // correct urlWithoutToken itself.
  return doWithToken((token) => Request.sendJSONReceiveJSON(`${urlWithoutToken}${token}`, data));
}
